import type { IOrganization } from '@/domain/entities/organization';
import { AddressVerificationLevel, type IMailingAddress } from '@/domain/shared/address';
import { OrgStatus } from '@/domain/shared/shared';
import type { IBusinessUser } from '@/domain/entities/user';
import { isValidEmail, type SupportedCountryCodes } from '@/lib';
import { formatNanpPhone, toE164FromNanp } from '@/lib/validation/phone';
import { normalizeName, normalizeOptional } from './organization';

export type OrganizationSharedFields = {
  name: string;
  website: string;
  email: string;
  phone: string;
  country: SupportedCountryCodes;
  addressLine1: string;
  addressLine2: string;
  city: string;
  region: string;
  postalCode: string;
};

export type OrganizationOnboardingDraft = OrganizationSharedFields & {
  slug: string;
  status: OrgStatus;
};

export type OrganizationDetailsDraft = OrganizationSharedFields & {
  slug: string;
  status: OrgStatus;
  capabilities: string[];
};

export type OrganizationSharedFieldErrors = Partial<
  Record<'name' | 'website' | 'email' | 'addressLine1' | 'city' | 'region' | 'postalCode', string | null>
>;

export function toSupportedCountry(value?: string | null): SupportedCountryCodes {
  return value?.toUpperCase() === 'US' ? 'US' : 'CA';
}

export function hasOrganizationAddressValues(
  values: Pick<OrganizationSharedFields, 'addressLine1' | 'addressLine2' | 'city' | 'region' | 'postalCode'>,
) {
  return Boolean(
    values.addressLine1.trim() ||
      values.addressLine2.trim() ||
      values.city.trim() ||
      values.region.trim() ||
      values.postalCode.trim(),
  );
}

export function getOrganizationRegionLabel(country: SupportedCountryCodes) {
  return country === 'CA' ? 'Province' : 'State';
}

export function normalizeOrgPhone(phone: string): string | undefined {
  const trimmed = phone.trim();
  if (!trimmed) return undefined;

  const digits = trimmed.replace(/\D/g, '');
  return digits.length >= 10 ? toE164FromNanp(trimmed) : trimmed;
}

export function isValidWebsite(value: string): boolean {
  try {
    const parsed = new URL(value);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
}

export function getOrganizationSharedFieldErrors(values: OrganizationSharedFields): OrganizationSharedFieldErrors {
  const website = values.website.trim();
  const email = values.email.trim();

  return {
    name: values.name.trim() ? null : null,
    website: !website || isValidWebsite(website) ? null : 'Enter a valid website URL (http:// or https://).',
    email: !email || isValidEmail(email) ? null : 'Enter a valid email address.',
    addressLine1: null,
    city: null,
    region: null,
    postalCode: null,
  };
}

export function buildOrganizationAddressFromFields(values: OrganizationSharedFields): IMailingAddress | undefined {
  if (!hasOrganizationAddressValues(values)) return undefined;

  const line1 = values.addressLine1.trim();
  const line2 = values.addressLine2.trim();
  const region = values.region.trim();
  const isCanada = values.country === 'CA';

  return {
    address: [line1, line2].filter(Boolean).join(', ') || null,
    city: values.city.trim() || null,
    state: isCanada ? null : region || null,
    province: isCanada ? region || null : null,
    postalCode: values.postalCode.trim() || null,
    country: values.country,
    addressVerificationLevel: AddressVerificationLevel.LEVEL_0,
  };
}

export function createOrganizationOnboardingDraft(args: {
  profileAddress?: IBusinessUser['address'];
  profileEmail?: string;
  profilePhone?: string;
  authEmail?: string | null;
}): OrganizationOnboardingDraft {
  const { profileAddress, profileEmail, profilePhone, authEmail } = args;

  return {
    name: '',
    slug: '',
    status: OrgStatus.Inactive,
    website: '',
    email: normalizeOptional(profileEmail) || normalizeOptional(authEmail),
    phone: profilePhone ? formatNanpPhone(profilePhone) : '',
    country: toSupportedCountry(profileAddress?.country),
    addressLine1: profileAddress?.line1 ?? '',
    addressLine2: profileAddress?.line2 ?? '',
    city: profileAddress?.city ?? '',
    region: profileAddress?.region ?? '',
    postalCode: profileAddress?.postalCode ?? '',
  };
}

export function mergeProfileSeedIntoOrganizationOnboardingDraft(
  draft: OrganizationOnboardingDraft,
  args: {
    profileAddress?: IBusinessUser['address'];
    profileEmail?: string;
    profilePhone?: string;
    authEmail?: string | null;
  },
): OrganizationOnboardingDraft {
  const next = { ...draft };

  const nextEmail = normalizeOptional(args.profileEmail) || normalizeOptional(args.authEmail);
  if (!next.email && nextEmail) {
    next.email = nextEmail;
  }

  const nextPhone = normalizeOptional(args.profilePhone);
  if (!next.phone && nextPhone) {
    next.phone = formatNanpPhone(nextPhone);
  }

  const nextCountry = toSupportedCountry(args.profileAddress?.country);
  if (!hasOrganizationAddressValues(next)) {
    next.country = nextCountry;
    if (args.profileAddress?.line1) next.addressLine1 = args.profileAddress.line1;
    if (args.profileAddress?.line2) next.addressLine2 = args.profileAddress.line2;
    if (args.profileAddress?.city) next.city = args.profileAddress.city;
    if (args.profileAddress?.region) next.region = args.profileAddress.region;
    if (args.profileAddress?.postalCode) next.postalCode = args.profileAddress.postalCode;
  }

  return next;
}

export function createOrganizationDetailsDraft(org: IOrganization): OrganizationDetailsDraft {
  return {
    name: org.name ?? '',
    slug: org.slug ?? '',
    status: org.status,
    website: org.website ?? '',
    email: org.email ?? '',
    phone: org.phone ? formatNanpPhone(org.phone) : '',
    country: toSupportedCountry(org.address?.country),
    addressLine1: org.address?.address ?? '',
    addressLine2: '',
    city: org.address?.city ?? '',
    region: org.address?.province ?? org.address?.state ?? '',
    postalCode: org.address?.postalCode ?? '',
    capabilities: Array.from(new Set(org.capabilities ?? [])),
  };
}

export function buildUpdatedOrganizationFromDetailsDraft(
  organization: IOrganization,
  draft: OrganizationDetailsDraft,
  args: {
    canEditStatus: boolean;
    canEditCapabilities: boolean;
  },
): IOrganization {
  return {
    ...organization,
    name: normalizeName(draft.name),
    slug: organization.slug,
    ownerID: organization.ownerID,
    status: args.canEditStatus ? draft.status : organization.status,
    website: normalizeOptional(draft.website) || undefined,
    email: normalizeOptional(draft.email) || undefined,
    phone: normalizeOrgPhone(draft.phone),
    address: buildOrganizationAddressFromFields(draft),
    capabilities: args.canEditCapabilities
      ? Array.from(new Set(draft.capabilities))
      : Array.from(new Set(organization.capabilities ?? [])),
  };
}
